// src/tattoo/Dashboard.jsx
import React from "react";
import toast from "react-hot-toast";
import {
  getRecentProjects,
  getRecentThumb,
  removeRecentProject,
  togglePinRecentProject,
  clearAllRecents,
} from "./projectsIndex";
import {
  loadProjectFromFile,
  loadProjectFromData,
  resetProjectToDefaults,
} from "./state";
import { restoreAutosaveIfAvailable } from "./autosave";
import tattuneLogo from "../assets/tt_horiz_logo.png";
import LZString from "lz-string";
import { idbGet } from "./idbAutosave.js";
import { useNavigate } from "react-router-dom";

function decodeSnapshot(raw) {
  if (!raw || typeof raw !== "string") return null;

  // old (raw JSON)
  if (raw.trim().startsWith("{")) {
    return JSON.parse(raw);
  }

  // new (compressed UTF16)
  const json = LZString.decompressFromUTF16(raw);
  if (!json) return null;
  return JSON.parse(json);
}

function stashViewSnapForProject(projectId) {
  try {
    const list = getRecentProjects() || [];
    const rec = list.find((x) => x?.id === projectId);
    const viewSnap = rec?.viewSnap || rec?.viewState || null;
    if (!viewSnap) return false;

    // ✅ this is what PreviewPane already reads in consumeUrlShare()
    window.__tt_pending_share = { cam: viewSnap };

    return true;
  } catch {
    return false;
  }
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= breakpoint;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const onResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    onResize(); // initial
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

const SNAPSHOT_KEY_PREFIX = "tt_project_snapshot:";
// anywhere shared (Dashboard/TattooApp), or just inline:
const SNAP_IDB_KEY = (projectId) => `__snapshot__:${projectId}`;
// Props:
// - onOpenProject()
// - onNewSession()

async function recentHasAnyLocalData(projectId) {
  if (!projectId) return false;

  // A) localStorage snapshot
  try {
    const key = `tt_project_snapshot:${projectId}`;
    if (localStorage.getItem(key)) return true;
  } catch {}

  // B) IDB snapshot (big projects)
  try {
    const rec = await idbGet(`__snapshot__:${projectId}`);
    if (rec?.payload && typeof rec.payload === "string") return true;
  } catch {}

  // C) IDB autosave fallback
  try {
    const rec = await idbGet(projectId);
    if (rec?.json && typeof rec.json === "string") return true;
  } catch {}

  return false;
}

function ConfirmModal({
  open,
  title,
  message,
  confirmText = "Remove",
  cancelText = "Cancel",
  danger = false,
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.62)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999999,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 16,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(16,16,18,0.96)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 24px 80px rgba(0,0,0,0.75)",
          padding: 16,
          color: "#f5f5f5",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>
          {title}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#cbd5e1",
            lineHeight: 1.35,
            marginBottom: 14,
          }}
        >
          {message}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            className="btn"
            type="button"
            onClick={onCancel}
            style={{
              padding: "16px 34px",
              borderRadius: 4,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "rgba(0,0,0,0.25)",
              color: "#e5e7eb",
              cursor: "pointer",
            }}
          >
            {cancelText}
          </button>

          <button
            className="btn"
            type="button"
            onClick={onConfirm}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: danger
                ? "1px solid rgba(239,68,68,0.55)"
                : "1px solid rgba(255,255,255,0.14)",
              background: danger
                ? "linear-gradient(135deg, rgba(127,29,29,0.95), rgba(2,6,23,0.95))"
                : "linear-gradient(135deg, rgba(30,30,30,0.85), rgba(2,6,23,0.85))",
              color: danger ? "#fee2e2" : "#e5e7eb",
              cursor: "pointer",
              boxShadow: danger ? "0 14px 34px rgba(185,28,28,0.38)" : "none",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ onOpenProject, onNewSession }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [recent, setRecent] = React.useState([]);
  const [canContinue, setCanContinue] = React.useState(false);
  const [confirm, setConfirm] = React.useState(null); // { type: "remove"|"clear", id? }
  const [removingIds, setRemovingIds] = React.useState(() => new Set());

  function markRemoving(id) {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }
  function unmarkRemoving(id) {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }
  // ---------- Load recent list + "can continue?" flag ----------
  React.useEffect(() => {
    let alive = true;

    (async () => {
      let r = getRecentProjects() || [];
      console.log("[Dashboard] loaded recent", r);

      // 🛡️ Phantom filter: after exhausting every debug angle we
      // couldn't identify the write path, but any entry that has the
      // default "Tattune" name AND no thumbnail AND a fresh-session ID
      // (`local:new:*`) is almost certainly a phantom from historical
      // corruption. Auto-hide + auto-clean these; the check is narrow
      // enough that real user projects won't be caught (users don't
      // name projects "Tattune", and any real project has a thumbnail
      // once captureAndIndexPreview has run).
      const isPhantom = (p) => {
        if (!p || typeof p !== "object") return true;
        const id = p.id || "";
        const name = (p.projectName || "").trim();
        const hasThumb =
          (p.thumb && p.thumb.length > 0) ||
          (p.thumbDataUrl && p.thumbDataUrl.length > 0);
        return (
          id.startsWith("local:new:") &&
          (name === "Tattune" || name === "Untitled" || name === "") &&
          !hasThumb
        );
      };
      const beforeCount = r.length;
      r = r.filter((p) => !isPhantom(p));
      if (r.length !== beforeCount) {
        console.warn(
          "[Dashboard] phantom filter removed",
          beforeCount - r.length,
          "orphan entries"
        );
        try {
          localStorage.setItem(
            "tattoo:recent-projects:v1",
            JSON.stringify(r)
          );
        } catch {}
      }

      // 1) prune ghosts (real recents whose underlying data has been deleted)
      const checks = await Promise.all(
        r.map(async (p) => ({ p, ok: await recentHasAnyLocalData(p?.id) }))
      );
      const valid = checks.filter((x) => x.ok).map((x) => x.p);

      if (valid.length !== r.length) {
        console.warn(
          "[Dashboard] pruned missing recents",
          r.length - valid.length
        );
        try {
          localStorage.setItem(
            "tattoo:recent-projects:v1",
            JSON.stringify(valid)
          );
        } catch {}
      }

      // 2) hydrate thumbs from IDB
      const withThumbs = await Promise.all(
        valid.map(async (p) => {
          const thumbDataUrl = await getRecentThumb(p?.id);
          return { ...p, thumbDataUrl: thumbDataUrl || null };
        })
      );

      // 3) sort pinned first, then newest
      const sorted = [...withThumbs].sort((a, b) => {
        const ap = a?.pinned ? 0 : 1;
        const bp = b?.pinned ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return (b?.updatedAt || 0) - (a?.updatedAt || 0);
      });

      if (!alive) return;
      setRecent(sorted);

      try {
        const hasPointer = !!localStorage.getItem(
          "tt_autosave_last_project_id"
        );
        setCanContinue(hasPointer || sorted.length > 0);
      } catch {
        setCanContinue(sorted.length > 0);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // ---------- File open ----------
  const fileInputRef = React.useRef(null);

  const handleBrowseFile = () => {
    if (!fileInputRef.current) {
      console.warn("[Dashboard] fileInputRef is null");
      return;
    }
    // let user pick same file twice in a row
    fileInputRef.current.value = "";
    console.log("[Dashboard] triggering file input click");
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    loadProjectFromFile(file);
    navigate("/app");
    if (typeof onOpenProject === "function") {
      onOpenProject();
    }
  };

  // ---------- Open recent snapshot ----------

  const handleOpenRecent = async (projectId) => {
    if (!projectId) return;
  
    const stamp = () => new Date().toISOString().slice(11, 19);
    const log = (...a) => console.log(`[OPEN-RECENT ${stamp()}]`, ...a);
  
    // --- helper: safe JSON parse ---
    const parseJSON = (s) => {
      try {
        return JSON.parse(s);
      } catch (e) {
        return { __parseError: String(e) };
      }
    };
  
    // --- helper: tiny fingerprint so we can compare quickly ---
    const fp = (data) => {
      if (!data || typeof data !== "object") return null;
      return {
        projectId: data.projectId,
        projectName: data.projectName,
        layers: Array.isArray(data.layers) ? data.layers.length : null,
        viewStateAt: data?.viewState?.at ?? null,
        viewSnapAt: data?.viewSnap?.at ?? null,
        updatedAt: data?.updatedAt ?? data?.meta?.updatedAt ?? null,
        version: data?.version ?? null,
      };
    };
  
    log("CLICK", { projectId });
  
    // 1) Read IDB autosave record (this is what enableAutosave writes)
    let idbRec = null;
    let idbData = null;
  
    try {
      idbRec = await idbGet(projectId);
      log("IDB GET", {
        hasRec: !!idbRec,
        keys: idbRec ? Object.keys(idbRec) : null,
        jsonLen: typeof idbRec?.json === "string" ? idbRec.json.length : null,
        projectIdInRec: idbRec?.projectId ?? null,
        projectNameInRec: idbRec?.projectName ?? null,
        sexInRec: idbRec?.sex ?? null,
      });
  
      if (typeof idbRec?.json === "string" && idbRec.json.length) {
        idbData = parseJSON(idbRec.json);
        log("IDB DATA FP", fp(idbData));
      }
    } catch (e) {
      log("IDB GET ERROR", e);
    }
  
    // 2) Read LS snapshot fallback
    let lsRaw = null;
    let lsData = null;
  
    try {
      const key = `${SNAPSHOT_KEY_PREFIX}${projectId}`;
      lsRaw = localStorage.getItem(key);
      log("LS SNAP", { key, has: !!lsRaw, len: lsRaw ? lsRaw.length : null });
  
      if (lsRaw) {
        lsData = decodeSnapshot(lsRaw);
        log("LS DATA FP", fp(lsData));
      }
    } catch (e) {
      log("LS READ ERROR", e);
    }
  
    // 3) Decide what we will load (keep it dumb for now)
    const chosen = idbData || lsData;
    log("CHOSEN", { source: idbData ? "idb-autosave" : lsData ? "ls-snapshot" : "none" });
  
    if (!chosen) {
      toast.error("No local snapshot found for this recent on this device.");
      return;
    }
  
    // 4) Load with explicit slot ID. Critical: this guarantees the
    // editor's tattoo.projectId is the slot the user clicked, so
    // subsequent autosaves write back to the same row instead of
    // whatever slot the previous session was attached to.
    try {
      loadProjectFromData(chosen, projectId);
      log("LOAD OK", fp(chosen));
    } catch (e) {
      log("LOAD FAILED", e);
      toast.error("Failed to load this project snapshot (see console logs).");
      return;
    }
  
    try {
      localStorage.setItem("tt_autosave_last_project_id", projectId);
    } catch {}
  
    onOpenProject?.();
    stashViewSnapForProject(projectId);
    navigate("/app");
  };
  
  

  const handleContinue = async () => {
    // 1) Try the most recent project snapshot from Recents (your existing logic)
    const recents = getRecentProjects();
    const last = recents && recents.length > 0 ? recents[0] : null;

    if (last && last.id) {
      try {
        const key = `${SNAPSHOT_KEY_PREFIX}${last.id}`;
        const raw = localStorage.getItem(key);

        if (raw) {
          const data = decodeSnapshot(raw);
          if (data) {
            console.log(
              "[Dashboard] handleContinue: using snapshot for project",
              last.id
            );
            // Pass the recent's id as the slot. Same anti-cross-contamination
            // guarantee as handleOpenRecent above.
            loadProjectFromData(data, last.id);

            // ✅ stash camera from recent meta (not from snapshot)
            stashViewSnapForProject(last.id);

            // ✅ go to studio
            navigate("/app");
            onOpenProject?.();
            return;
          }
        }
      } catch (e) {
        console.warn(
          "[Dashboard] handleContinue: snapshot load failed, will try autosave",
          e
        );
      }
    }

    // 2) Fallback to autosave (NOW ASYNC)
    const ok = await restoreAutosaveIfAvailable();
    if (ok) {
      typeof onOpenProject === "function" && onOpenProject();
    } else {
      toast.error("No autosaved session or project snapshot found.");
    }
  };

  const formatDateTime = (ms) => {
    if (!ms) return "";
    const d = new Date(ms);
    const date = d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${date} · ${time}`;
  };

  const hasRecents = recent && recent.length > 0;

  // ---------- UI ----------
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "32px 16px",
        boxSizing: "border-box",
        background: "#000000",
        color: "#f5f5f5",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: isMobile ? 500 : 960,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 28,
          alignItems: "center",
        }}
      >
        {/* Logo / title */}
        <header
          style={{
            textAlign: "center",
            marginBottom: 4,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "25px 25px",
            }}
          >
            {tattuneLogo && (
              <img
                src={tattuneLogo}
                alt="Tattune"
                style={{
                  height: 112,
                  width: "auto",
                  display: "block",
                }}
              />
            )}
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: "#22c55e",
                boxShadow: "0 0 15px 15px rgba(36, 178, 86, 0.15)",
              }}
            />
            
          </div>
{/*
          <h1
            style={{
              marginTop: 18,
              marginBottom: 4,
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: 0.02,
            }}
          >
            FROM THINK TO INK
          </h1> */}
          <p
            style={{
              margin: "auto",
              fontSize: 11,
              color: "#bbb",
              width: 210,

            }}
          >
            Continue a session, open a saved <code>.tattune</code>, or start a
            fresh session.
          </p>
        </header>

        {/* Primary actions */}
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            alignItems: "center",
            width: "100%",
            maxWidth: 720,
          }}
        >
          {/* Main CTA row */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              justifyContent: "center",
            }}
          >
            {/*<button
              className="btn"
              style={{
                padding: "10px 20px",
                minWidth: 170,
                borderRadius: 999,
                border: "1px solid #b91c1c",
                background: canContinue
                  ? "linear-gradient(135deg, #7f1d1d 0%, #111827 55%, #020617 100%)"
                  : "rgba(30,30,30,0.85)",
                color: canContinue ? "#fee2e2" : "#666",
                cursor: canContinue ? "pointer" : "default",
                boxShadow: canContinue
                  ? "0 16px 40px rgba(185,28,28,0.5)"
                  : "none",
                opacity: canContinue ? 1 : 0.6,
              }}
              onClick={canContinue ? handleContinue : undefined}
              disabled={!canContinue}
            >
              {canContinue ? "Continue last session" : "No saved session found"}
            </button>*/}

            <button
              className="btn"
              style={{
                padding: "9px 16px",
                minWidth: 170,
                borderRadius: 999,
                border: "1px solid #404040",
                background: "linear-gradient(135deg, #171717 0%, #020617 100%)",
                color: "#e5e5e5",
                cursor: "pointer",
              }}
              onClick={handleBrowseFile}
            >
              Open .tattune file…
            </button>

            <button
              className="btn btn-primary"
              style={{
                padding: "9px 16px",
                minWidth: 170,
                borderRadius: 999,
                border: "1px solid #404040",
                background:
                  "radial-gradient(circle at 0% 0%, #991b1b 0, #020617 55%)",
                color: "#fee2e2",
                cursor: "pointer",
                boxShadow: "0 10px 28px rgba(127,29,29,0.6)",
              }}
              onClick={() => {
                if (typeof onNewSession === "function") {
                  onNewSession();
                }
              }}
            >
              + Start a New Session
            </button>
            <button
              className="btn"
              style={{
                padding: "9px 16px",
                minWidth: 170,
                borderRadius: 999,
                border: "1px solid #404040",
                background: "linear-gradient(135deg, #111827 0%, #020617 100%)",
                color: "#e5e5e5",
                cursor: "pointer",
              }}
              onClick={() => setConfirm({ type: "clear" })}
            >
              Clear recents…
            </button>
          </div>

          {/* Hidden file input (works on iOS, allows .tattune) */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            style={{
              position: "absolute",
              left: "-9999px",
              top: 0,
              opacity: 0,
              width: 0,
              height: 0,
            }}
          />
        </section>

        {/* Recent projects */}
        <section
          style={{
            width: "100%",
            maxWidth: 720,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: 10,
          }}
        >
          <h2
            style={{
              fontSize: 16,
              marginBottom: 10,
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "#e5e7eb",
            }}
          >
            Recent projects
          </h2>

          {!hasRecents ? (
            <div
              style={{
                color: "#9ca3af",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              No recent projects yet — once you work on a file, it’ll appear
              here on this device.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(auto-fill, minmax(150px, 1fr))"
                  : "repeat(auto-fill, minmax(220px, 1fr))",
                gap: isMobile ? 12 : 16,
                width: "100%",
                padding: 4,
              }}
            >
              {recent.map((p) => {
                const thumbSrc = p.thumbDataUrl || p.thumb || null;
                const isRemoving = removingIds.has(p.id);
                return (
                  <article
                    key={p.id}
                    onClick={() => handleOpenRecent(p.id)}
                    style={{
                      borderRadius: 16,
                      position: "relative",
                      border: "1px solid #3f3f46",
                      background:
                        "linear-gradient(145deg, #050508 0%, #101014 45%, #050509 100%)",
                      overflow: "hidden",
                      display: "flex",
                      opacity: isRemoving ? 0 : 1,
                      transform: isRemoving ? "scale(0.98)" : "translateY(0px)",
                      filter: isRemoving ? "blur(1px)" : "none",
                      pointerEvents: isRemoving ? "none" : "auto",
                      transition:
                        "transform 180ms ease, opacity 180ms ease, filter 180ms ease, border-color 120ms ease, box-shadow 120ms ease",
                      flexDirection: "column",
                      cursor: "pointer",
                      boxShadow: "0 14px 32px rgba(0,0,0,0.6)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#b91c1c";
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 20px 46px rgba(0,0,0,0.8)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#3f3f46";
                      e.currentTarget.style.transform = "translateY(0px)";
                      e.currentTarget.style.boxShadow =
                        "0 14px 32px rgba(0,0,0,0.6)";
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirm({
                          type: "remove",
                          id: p.id,
                          name: p.projectName,
                        });
                      }}
                      style={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        border: "1px solid rgba(239,68,68,0.55)",
                        background: "rgba(127,29,29,0.9)", // 🔴 red circle
                        color: "#fee2e2",
                        fontSize: 18,
                        fontWeight: 800,
                        cursor: "pointer",
                        lineHeight: "28px",
                        display: "grid",
                        placeItems: "center",
                        boxShadow: "0 10px 24px rgba(185,28,28,0.35)",
                      }}
                      title="Remove project"
                    >
                      ×
                    </button>
                    <div
                      style={{
                        background: "#18181b",
                        aspectRatio: isMobile ? "4 / 5" : "3 / 4",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {thumbSrc ? (
                        <img
                          src={thumbSrc}
                          alt={p.projectName}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            color: "#6b7280",
                            fontSize: 12,
                            textAlign: "center",
                            padding: 12,
                          }}
                        >
                          No thumbnail captured yet
                        </div>
                      )}
                    </div>

                    <div style={{ padding: 12 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          marginBottom: 4,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "#f9fafb",
                        }}
                      >
                        {p.projectName || "Untitled"}
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: "#9ca3af",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 10,
                        }}
                      >
                        <span>
                          {(() => {
                            const sex = (p.sex || "").toString().toLowerCase();
                            const isMale = sex.startsWith("m");
                            return isMale ? "♂ Male" : "♀ Female";
                          })()}
                        </span>
                        <span>{formatDateTime(p.updatedAt)}</span>
                      </div>

                      <button
                        className="btn btn-small"
                        style={{
                          width: "100%",
                          padding: "7px 10px",
                          borderRadius: 999,
                        }}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenRecent(p.id);
                        }}
                      >
                        Open Studio
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: "#bbb",
          }}
        >
          © 2026 All Rights Reserved
        </p>
      </div>
      <ConfirmModal
        open={!!confirm}
        title={
          confirm?.type === "clear"
            ? "Clear recent projects?"
            : "Remove recent project?"
        }
        message={
          confirm?.type === "clear"
            ? "This removes all recents from this device. It does not delete any saved .tattune files."
            : `Remove "${
                confirm?.name || "this project"
              }" from this device?\n\n(This does not delete any saved .tattune files.)`
        }
        confirmText={confirm?.type === "clear" ? "Clear all" : "Remove"}
        cancelText="Cancel"
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const c = confirm;
          setConfirm(null);
          if (!c) return;

          if (c.type === "clear") {
            await clearAllRecents();
            setRecent([]);
            return;
          }

          if (c.type === "remove" && c.id) {
            markRemoving(c.id);

            // let fade play
            setTimeout(async () => {
              await removeRecentProject(c.id);
              setRecent((prev) => prev.filter((x) => x.id !== c.id));
              unmarkRemoving(c.id);
            }, 160);
          }
        }}
      />
    </div>
  );
}
